import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import { urgentConcernsApi } from "app/services/api/urgentConcernsApi";
import { shiftHandoverApi } from "app/services/api/shiftHandoverApi";
import { UrgentConcernsIndexModel } from "app/services/api/types/urgentConcerns/models/UrgentConcernsIndexModel";
import { UrgentConcernsIndexResponse } from "app/services/api/types/urgentConcerns/responses/UrgentConcernsIndexResponse";
import { RootState } from "app/services/store";

type ResidentId = number;

interface State {
  urgentConcerns: Record<ResidentId, UrgentConcernsIndexModel[]>;
}

const initialState: State = {
  urgentConcerns: {},
};

export const urgentConcernsSlice = createSlice({
  name: "urgentConcerns",
  initialState,
  reducers: {
    storeResidentUrgentConcerns: (
      state,
      action: PayloadAction<{
        residentId: number;
        data: UrgentConcernsIndexResponse;
      }>,
    ) => {
      const { residentId, data } = action.payload;
      state.urgentConcerns[residentId] = data.data;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      urgentConcernsApi.endpoints.getResidentUrgentConcernsIndex.matchFulfilled,
      (state, action) => {
        const { residentId } = action.meta.arg.originalArgs;
        state.urgentConcerns[residentId] = action.payload.data;
      },
    );

    builder.addMatcher(
      shiftHandoverApi.endpoints.addUrgentConcern.matchFulfilled,
      (state, action) => {
        const residentId = action.meta.arg.originalArgs.body.resident_id;
        delete state.urgentConcerns[residentId];
      },
    );

    builder.addMatcher(
      shiftHandoverApi.endpoints.deleteUrgentConcern.matchFulfilled,
      (state, action) => {
        const { urgentConcernId } = action.meta.arg.originalArgs;
        for (const residentId of Object.keys(state.urgentConcerns)) {
          state.urgentConcerns[Number(residentId)] = state.urgentConcerns[
            Number(residentId)
          ].filter((c) => c.id !== urgentConcernId);
        }
      },
    );

    builder.addMatcher(
      shiftHandoverApi.endpoints.updateUrgentConcernStatus.matchFulfilled,
      (state, action) => {
        const { id } = action.meta.arg.originalArgs.params;
        const { status, comment } = action.meta.arg.originalArgs.body;
        for (const residentId of Object.keys(state.urgentConcerns)) {
          const concern = state.urgentConcerns[Number(residentId)]?.find(
            (c) => c.id === id,
          );
          if (concern) {
            concern.status = status;
            if (comment !== undefined) concern.comment = comment;
          }
        }
      },
    );
  },
});

export const { storeResidentUrgentConcerns } = urgentConcernsSlice.actions;

export const selectResidentUrgentConcerns = createSelector(
  [
    (state: RootState) => state.urgentConcerns?.urgentConcerns,
    (_: RootState, residentId?: number) => residentId,
  ],
  (urgentConcerns, residentId) =>
    !residentId ? [] : urgentConcerns?.[residentId] || [],
);

export default urgentConcernsSlice.reducer;
